"use client";

import { Button, cn, DataTable, type TanstackReactTable } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeftIcon,
  ChevronRight,
  History,
} from "lucide-react";
import { useMemo, useRef } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { InventoryMovementTypeDTO } from "@/modules/merchant/domain/contracts";
import type { ShopInventoryMovementRow } from "@/modules/merchant/presentation/api/client";
import {
  useShopInventoryMovementsQuery,
  useShopInventoryReconcileQuery,
} from "@/modules/merchant/presentation/api/queries";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import {
  movementTypeBadge,
  ShopInventoryMovementsFilter,
} from "@/modules/merchant/presentation/ui/ShopInventoryMovementsFilter";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";

const INVENTORY_ROUTE = "/app/shops/$shopId/inventory" as const;
const INVENTORY_MOVEMENTS_ROUTE =
  "/app/shops/$shopId/inventory/movements" as const;

export function ShopInventoryMovementsPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: INVENTORY_MOVEMENTS_ROUTE });
  const search = useSearch({ from: INVENTORY_MOVEMENTS_ROUTE });

  const numberFmt = useMemo(() => new Intl.NumberFormat("lo-LA"), []);
  const reconcileDetailRef = useRef<HTMLDivElement>(null);

  const movements = useShopInventoryMovementsQuery(shopId, {
    offset: search.offset,
    limit: search.limit,
    branchId: search.branchId,
    shopProductSkuId: search.shopProductSkuId,
    type: search.type as InventoryMovementTypeDTO | undefined,
    createdFrom: search.createdFrom,
    createdTo: search.createdTo,
  });

  const reconcile = useShopInventoryReconcileQuery(shopId, {
    branchId: search.branchId,
    onlyMismatch: search.recOnlyMismatch,
    limit: 100,
  });

  const movementRows = movements.data?.data ?? [];
  const movementTotal = movements.data?.meta.total ?? 0;

  const reconcileSummary = reconcile.data?.summary;
  const mismatchRowCount =
    (reconcileSummary?.lotMismatches ?? 0) +
    (reconcileSummary?.serialMismatches ?? 0);
  const hasMismatch =
    !reconcile.isError && !reconcile.isLoading && mismatchRowCount > 0;

  const scrollToReconcileDetail = () => {
    const el = reconcileDetailRef.current;
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const investigateDrift = () => {
    if (!shopId) return;
    if (!search.recOnlyMismatch) {
      nav({
        to: INVENTORY_MOVEMENTS_ROUTE,
        params: { shopId },
        search: { ...search, recOnlyMismatch: true },
      });
      window.setTimeout(scrollToReconcileDetail, 280);
    } else {
      scrollToReconcileDetail();
    }
  };

  const movementColumns: TanstackReactTable.ColumnDef<ShopInventoryMovementRow>[] =
    useMemo(
      () => [
        {
          id: "at",
          header: "ເວລາ",
          cell: ({ row }) => (
            <span className="text-muted-foreground text-xs tabular-nums">
              {formatDateTimeLocal(new Date(row.original.createdAt))}
            </span>
          ),
          size: 132,
        },
        {
          id: "branch",
          header: "ສາຂາ",
          cell: ({ row }) => (
            <span className="text-sm">{row.original.branchName}</span>
          ),
          size: 120,
        },
        {
          id: "product",
          header: "ສິນຄ້າ",
          cell: ({ row }) => {
            const r = row.original;
            return (
              <div className="min-w-0 max-w-[220px]">
                <p className="truncate font-medium text-sm">{r.productName}</p>
                <p className="truncate font-mono text-muted-foreground text-xs">
                  {r.shopSku}
                </p>
              </div>
            );
          },
          size: 220,
        },
        {
          id: "type",
          header: "ປະເພດ",
          cell: ({ row }) => movementTypeBadge(row.original.type),
          size: 120,
        },
        {
          id: "qty",
          header: "ຈຳນວນ",
          cell: ({ row }) => {
            const q = row.original.qty;
            const sign = q > 0 ? "+" : "";
            const colorClass =
              q === 0
                ? "text-muted-foreground"
                : q > 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-destructive";
            return (
              <span
                className={cn("font-semibold text-sm tabular-nums", colorClass)}
              >
                {sign}
                {numberFmt.format(q)}
              </span>
            );
          },
          size: 88,
        },
        {
          id: "ref",
          header: "ອ້າງອີງ",
          cell: ({ row }) => {
            const r = row.original;
            if (!r.referenceType && !r.referenceId) {
              return <span className="text-muted-foreground text-xs">—</span>;
            }
            return (
              <span className="break-all font-mono text-[11px] text-muted-foreground">
                {r.referenceType ?? "—"}
                {r.referenceId ? ` · ${r.referenceId.slice(0, 12)}` : ""}
              </span>
            );
          },
          size: 120,
        },
        {
          id: "note",
          header: "ໝາຍເຫດ",
          cell: ({ row }) => {
            const n = row.original.note;
            if (!n) {
              return <span className="text-muted-foreground text-xs">—</span>;
            }
            const short = n.length > 48 ? `${n.slice(0, 48)}…` : n;
            return (
              <span className="text-muted-foreground text-xs" title={n}>
                {short}
              </span>
            );
          },
          size: 140,
        },
        {
          id: "actor",
          header: "ຜູ້ບັນທຶກ",
          cell: ({ row }) => (
            <span className="text-muted-foreground text-xs">
              {row.original.createdByName ?? row.original.createdBy ?? "—"}
            </span>
          ),
          size: 100,
        },
        {
          id: "track",
          header: "Lot / Serial",
          cell: ({ row }) => {
            const r = row.original;
            if (!r.lotId && !r.serialId) {
              return <span className="text-muted-foreground text-xs">—</span>;
            }
            return (
              <div className="font-mono text-[10px] text-muted-foreground leading-tight">
                {r.lotId ? <div>lot: {r.lotId.slice(0, 8)}…</div> : null}
                {r.serialId ? <div>sn: {r.serialId.slice(0, 8)}…</div> : null}
              </div>
            );
          },
          size: 96,
        },
      ],
      [numberFmt],
    );

  return (
    <RequireMerchantPermission permission={MerchantPermissions.inventory.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.inventoryMovements(shopId)} />
      </Header>

      <Main>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="flex items-center gap-2 font-heading font-semibold text-xl tracking-tight">
              <History className="size-6 text-primary" aria-hidden />
              ປະຫວັດການເຄື່ອນໄຫວສະຕ໋ອກ
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ກອງຕາມສາຂາ, SKU, ປະເພດ ແລະ ຊ່ວງວັນທີ — ກວດສອບຍອດ lot/serial ກັບຄົງເຫຼືອ
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 gap-1 self-start"
            type="button"
            onClick={() => {
              if (!shopId) return;
              nav({ to: INVENTORY_ROUTE, params: { shopId } });
            }}
          >
            <ArrowLeftIcon className="size-4" aria-hidden />
            ກັບສາງ
          </Button>
        </div>

        <section className="mb-8 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <div className="border-l-4 border-l-sky-600">
            <div className="flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-stretch lg:gap-8">
              <div className="grid flex-1 grid-cols-1 gap-6 sm:grid-cols-3 sm:gap-4">
                <div className="flex flex-col gap-1">
                  <p className="font-heading font-semibold text-[11px] text-muted-foreground uppercase tracking-[0.14em]">
                    ຄົງເຫຼືອ (ບັນທຶກ)
                  </p>
                  <p className="font-bold font-heading text-2xl text-foreground tabular-nums tracking-tight">
                    {reconcile.isLoading || reconcile.isError
                      ? "—"
                      : numberFmt.format(
                          reconcileSummary?.totalOnHandTracked ?? 0,
                        )}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <p className="font-heading font-semibold text-[11px] text-muted-foreground uppercase tracking-[0.14em]">
                    ຜົນລວມ lot
                  </p>
                  <p className="font-bold font-heading text-2xl text-foreground tabular-nums tracking-tight">
                    {reconcile.isLoading || reconcile.isError
                      ? "—"
                      : numberFmt.format(
                          reconcileSummary?.totalLotDerivedQty ?? 0,
                        )}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <p className="font-heading font-semibold text-[11px] text-muted-foreground uppercase tracking-[0.14em]">
                    Serial ຢູ່ສາງ
                  </p>
                  <p className="font-bold font-heading text-2xl text-foreground tabular-nums tracking-tight">
                    {reconcile.isLoading || reconcile.isError
                      ? "—"
                      : numberFmt.format(
                          reconcileSummary?.totalSerialInStock ?? 0,
                        )}
                  </p>
                </div>
              </div>

              <div className="flex w-full flex-col justify-center lg:max-w-sm lg:border-border lg:border-l lg:pl-8">
                {reconcile.isError ? (
                  <p className="text-destructive text-sm">ໂຫຼດສະຫຼຸບບໍ່ສຳເລັດ</p>
                ) : reconcile.isLoading ? (
                  <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ…</p>
                ) : hasMismatch ? (
                  <div className="rounded-lg border border-destructive/25 bg-destructive/5 p-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle
                        className="mt-0.5 size-5 shrink-0 text-destructive"
                        aria-hidden
                      />
                      <div className="min-w-0 space-y-2">
                        <p className="font-heading font-semibold text-destructive text-sm">
                          ພົບຄວາມບໍ່ກົງກັນ
                        </p>
                        <p className="text-foreground/90 text-sm leading-relaxed">
                          ຄວາມຕ່າງລວມປະມານ{" "}
                          <span className="font-semibold text-foreground tabular-nums">
                            {numberFmt.format(
                              reconcileSummary?.mismatchUnitDelta ?? 0,
                            )}
                          </span>{" "}
                          ຫົວຕໍ່ແຖວທີ່ບໍ່ກົງ ({numberFmt.format(mismatchRowCount)}{" "}
                          ລາຍການ) — lot:{" "}
                          {numberFmt.format(
                            reconcileSummary?.lotMismatches ?? 0,
                          )}
                          , serial:{" "}
                          {numberFmt.format(
                            reconcileSummary?.serialMismatches ?? 0,
                          )}
                          .
                        </p>
                        <Button
                          type="button"
                          variant="link"
                          className="h-auto p-0 font-semibold text-secondary"
                          onClick={investigateDrift}
                        >
                          ກວດສອບລາຍລະອຽດ
                          <ChevronRight className="ml-0.5 size-4" aria-hidden />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-emerald-600/20 bg-emerald-500/5 p-4">
                    <p className="font-medium text-emerald-800 text-sm dark:text-emerald-200">
                      ບໍ່ພົບຄວາມບໍ່ກົງກັນໃນຂອບເຂດທີ່ເລືອກ
                    </p>
                    <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
                      ຄົງເຫຼືອກົງກັບຜົນລວມ lot ແລະ serial ທີ່ນັບໄດ້
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <div className="mb-10 flex flex-col rounded-xl border bg-card pt-2">
          <ShopInventoryMovementsFilter />
          <DataTable<ShopInventoryMovementRow, unknown>
            noDataMessage="ບໍ່ມີລາຍການທີ່ກົງກັບຕົວກອງ."
            isLoading={movements.isLoading}
            columns={movementColumns}
            data={movementRows}
            offset={search.offset}
            limit={search.limit}
            totalCount={movementTotal}
            onPaginationChange={(p) => {
              if (!shopId) return;
              nav({
                to: INVENTORY_MOVEMENTS_ROUTE,
                params: { shopId },
                search: { ...search, offset: p.offset, limit: p.limit },
              });
            }}
          />
        </div>
      </Main>
    </RequireMerchantPermission>
  );
}
